import { useEffect, useState, type ReactNode } from 'react';
import { useUiStore } from '../store/ui';
import { Button } from './core';

/* ---------------- Modal / Confirm ---------------- */

export function Modal(props: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}) {
  return (
    <div className="modal-backdrop" onClick={() => props.onClose()}>
      <div
        className={`modal ${props.className ?? ''}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>{props.title}</h3>
          <button className="icon-button" aria-label="Close" onClick={props.onClose}>
            ✕
          </button>
        </div>
        <div className="mt-2">{props.children}</div>
        {props.footer ? <div className="mt-4 flex justify-between">{props.footer}</div> : null}
      </div>
    </div>
  );
}

export function ConfirmDialog(props: {
  // Optional: callers either render conditionally ({x && <ConfirmDialog/>}) or
  // pass open explicitly — both styles must work (an unconditional `open`
  // requirement silently hid every conditionally-rendered confirm).
  open?: boolean;
  title: string;
  body: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (props.open === false) return null;
  return (
    <Modal
      title={props.title}
      onClose={props.onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={props.onCancel}>Cancel</Button>
          <Button variant="danger" onClick={props.onConfirm}>{props.confirmLabel ?? 'Confirm'}</Button>
        </>
      }
    >
      <p>{props.body}</p>
    </Modal>
  );
}

/* ---------------- Table / Pagination / Tabs ---------------- */

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
}
export function DataTable<T>(props: { columns: Column<T>[]; rows: T[]; onRowClick?: (row: T) => void }) {
  if (props.rows.length === 0) return null;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            {props.columns.map((c) => <th key={c.key}>{c.header}</th>)}
          </tr>
        </thead>
        <tbody>
          {props.rows.map((row, ri) => (
            <tr
              key={ri}
              className={props.onRowClick ? 'clickable' : undefined}
              onClick={props.onRowClick ? () => props.onRowClick(row) : undefined}
            >
              {props.columns.map((c) => <td key={c.key}>{c.render(row)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Pagination(props: {
  page: number;
  totalPages: number;
  onPage: (p: number) => void;
}) {
  if (props.totalPages <= 1) return null;
  return (
    <div className="pagination" role="navigation" aria-label="Pagination">
      <Button variant="secondary" size="sm" disabled={props.page <= 1} onClick={() => props.onPage(props.page - 1)}>
        ‹ Prev
      </Button>
      <span className="text-muted text-sm">Page {props.page} of {props.totalPages}</span>
      <Button variant="secondary" size="sm" disabled={props.page >= props.totalPages} onClick={() => props.onPage(props.page + 1)}>
        Next ›
      </Button>
    </div>
  );
}

export function Tabs(props: {
  /** `icon` renders inside the tab button; `badge` is a compact count chip. */
  tabs: { id: string; label: string; icon?: ReactNode; badge?: string | number }[];
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="tabs" role="tablist">
      {props.tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          aria-selected={t.id === props.active}
          className={`tab ${t.id === props.active ? 'active' : ''}`}
          onClick={() => props.onChange(t.id)}
        >
          {t.icon ? <span className="tab-icon">{t.icon}</span> : null}
          <span className="tab-label">{t.label}</span>
          {t.badge === undefined || t.badge === null ? null : <span className="tab-badge">{t.badge}</span>}
        </button>
      ))}
    </div>
  );
}

/* ---------------- Toasts ---------------- */

export function Toasts() {
  const { toasts, dismissToast } = useUiStore();
  if (toasts.length === 0) return null;
  return (
    <div className="toasts" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className="toast" onClick={() => dismissToast(t.id)}>
          <div className="toast-title">{t.title}</div>
          {t.message ? <div className="toast-message">{t.message}</div> : null}
        </div>
      ))}
    </div>
  );
}

/* ---------------- misc helpers ---------------- */

export function SpinnerBlock(props: { label?: string }) {
  return (
    <div className="flex items-center gap-2 mt-4">
      <span className="spinner" role="status" />
      <span className="text-muted">{props.label ?? 'Loading…'}</span>
    </div>
  );
}

/** Re-renders every `interval` ms; returns a tick counter for "live" time. */
export function useNow(interval = 1000): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), interval);
    return () => clearInterval(t);
  }, [interval]);
  return now;
}

export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

export function formatKbps(kbps: number | null | undefined): string {
  if (!kbps) return '—';
  return kbps >= 1000 ? `${(kbps / 1000).toFixed(1)} Mbps` : `${kbps} Kbps`;
}